import { useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  ExternalLink,
  GitMerge,
  Globe2,
  Handshake,
  Mail,
  Pencil,
  Phone,
  Plus,
  Users,
} from "lucide-react";
import { useStore } from "../lib/store";
import { showFeedback } from "../lib/feedback";
import { formatCurrency } from "../lib/utils";
import type { Company, PartyChannel } from "../lib/types";
import {
  COMPANY_VIEWS,
  buildCompanyRows,
  companySegments,
  matchesCompanyView,
  searchCompanyRows,
  sortCompanyRows,
  summarizeCompanies,
  type CompanyRow,
  type CompanySortKey,
  type CompanyViewId,
  type SortDirection,
} from "../features/identity/empresas";
import {
  channelsInCompanyHistory,
  contactsInCompanyHistory,
  identityContextForLead,
  leadsInCompanyHistory,
} from "../features/identity/queries";
import { companyCnpjDecision } from "../features/identity/identity";
import {
  MergeCompaniesDialog,
  ReclassifyChannelDialog,
  ReviewCandidateDialog,
} from "../features/identity/IdentityDialogs";
import { useIdentityGate } from "../features/identity/useIdentityGate";
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, pageCount, paginate } from "../features/leads/list";
import { PageHead, Toolbar, ToolbarSpacer } from "../ui/PageHead";
import { SideNav, type SideNavGroup } from "../ui/SideNav";
import { DataTable, type DataTableColumn } from "../ui/DataTable";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { Select } from "../ui/Select";
import { SearchInput } from "../ui/SearchInput";
import { EmptyState } from "../ui/EmptyState";
import { Avatar } from "../ui/Avatar";
import { Pill } from "../ui/Pill";
import { Dialog } from "../ui/Dialog";
import { Field, TextInput } from "../ui/Field";
import { appPath } from "../app/routes";
import "./empresas.css";

/**
 * Empresas — a identidade empresarial da V2.
 *
 * A empresa é a identidade global; o lead é a oportunidade dela em um funil
 * (AGENTS §27.2, §27.10). Por isso esta tela agrega contatos, canais e
 * oportunidades sem nunca juntar jornadas: cada lead continua com a sua.
 *
 * Tudo aqui depende do contrato server-side de capacidades. Quando a fundação
 * não está instalada ou está degradada, a superfície continua visível e diz o
 * motivo, em vez de sumir ou de simular escrita (§18).
 */

const SORT_LABELS: Record<CompanySortKey, string> = {
  empresa: "Empresa",
  segmento: "Segmento",
  contatos: "Contatos",
  canais: "Canais",
  oportunidades: "Oportunidades",
  valor: "Em negociação",
};

function UnavailableIdentity({ reason, degraded }: { reason: string; degraded: boolean }) {
  return (
    <div className="cx-empresas">
      <section className="cx-work">
        <PageHead
          title="Empresas"
          sub="Identidade empresarial, contatos, canais e oportunidades agregadas."
        />
        <EmptyState
          variant={degraded ? "error" : "unavailable"}
          icon={<Building2 size={22} aria-hidden="true" />}
          title={degraded ? "Identidade empresarial degradada" : "Identidade empresarial indisponível"}
          description={reason}
        />
      </section>
    </div>
  );
}

export function EmpresasPage() {
  const store = useStore();
  const gate = useIdentityGate();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);

  const view = (params.get("visao") as CompanyViewId | null) ?? "todas";
  const query = params.get("q") ?? "";
  const segment = params.get("segmento") ?? "todos";
  const sortKey = (params.get("ordem") as CompanySortKey | null) ?? "empresa";
  const sortDirection = (params.get("dir") as SortDirection | null) ?? "asc";
  const page = Math.max(1, Number(params.get("pagina") ?? 1) || 1);
  const pageSize = PAGE_SIZES.includes(Number(params.get("tamanho")) as (typeof PAGE_SIZES)[number])
    ? Number(params.get("tamanho"))
    : DEFAULT_PAGE_SIZE;

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    if (!("pagina" in patch)) next.delete("pagina");
    setParams(next, { replace: true });
  };

  const rows = useMemo(
    () =>
      buildCompanyRows({
        companies: store.companies,
        contacts: store.contacts,
        partyChannels: store.partyChannels,
        leads: store.leads,
        memberships: store.memberships,
      }),
    [store.companies, store.contacts, store.partyChannels, store.leads, store.memberships],
  );

  // A mesma seleção alimenta o contador do menu e a tabela: o número da visão é
  // sempre o número de linhas que ela mostra (AGENTS §10).
  const searched = useMemo(
    () => searchCompanyRows({ rows, companies: store.companies, query, segment }),
    [rows, store.companies, query, segment],
  );
  const selection = useMemo(
    () => searched.filter((row) => matchesCompanyView(row, view)),
    [searched, view],
  );
  const sorted = useMemo(
    () => sortCompanyRows(selection, sortKey, sortDirection),
    [selection, sortKey, sortDirection],
  );
  const pages = pageCount(sorted.length, pageSize);
  const current = Math.min(page, pages);
  const visible = paginate(sorted, current, pageSize);
  const summary = summarizeCompanies(selection);
  const segments = useMemo(() => companySegments(rows), [rows]);

  const sideGroups: SideNavGroup[] = [
    {
      id: "visoes",
      label: "Visões",
      items: COMPANY_VIEWS.map((item) => ({
        id: item.id,
        label: item.label,
        count: searched.filter((row) => matchesCompanyView(row, item.id)).length,
      })),
    },
  ];

  const columns: Array<DataTableColumn<CompanyRow>> = [
    {
      id: "empresa",
      label: "Empresa",
      width: 260,
      sortKey: "empresa",
      cell: (row) => (
        <span className="cx-company-id">
          <Avatar name={row.name} />
          <span className="cx-company-names">
            <button
              type="button"
              className="cx-company-name"
              onClick={() => navigate(`${appPath("empresas")}/${row.company.id}`)}
            >
              {row.name}
            </button>
            {(row.legalName !== row.name || row.company.cnpj) && (
              <span className="cx-company-legal" title={row.legalName}>
                {row.legalName !== row.name ? row.legalName : row.company.cnpj}
              </span>
            )}
          </span>
        </span>
      ),
    },
    {
      id: "segmento",
      label: "Segmento",
      width: 150,
      sortKey: "segmento",
      cell: (row) => <span className="cx-company-meta">{row.segment || "—"}</span>,
    },
    {
      id: "local",
      label: "Localização",
      width: 140,
      cell: (row) => <span className="cx-company-meta">{row.location || "—"}</span>,
    },
    {
      id: "contatos",
      label: "Contatos",
      width: 100,
      numeric: true,
      sortKey: "contatos",
      cell: (row) => <span className="cx-data">{row.canonical ? row.contacts.length : "—"}</span>,
    },
    {
      id: "canais",
      label: "Canais",
      width: 90,
      numeric: true,
      sortKey: "canais",
      cell: (row) => <span className="cx-data">{row.canonical ? row.channels.length : "—"}</span>,
    },
    {
      id: "oportunidades",
      label: "Oportunidades",
      width: 210,
      sortKey: "oportunidades",
      cell: (row) => (
        <span className="cx-company-pipeline">
          <Pill tone={row.active ? "accent" : "neutral"} dot>
            {row.active} aberta{row.active === 1 ? "" : "s"}
          </Pill>
          {/* Zero não vira texto: só os desfechos que existem são ditos. */}
          <span>
            {[
              row.won ? `${row.won} ganha${row.won === 1 ? "" : "s"}` : null,
              row.lost ? `${row.lost} perdida${row.lost === 1 ? "" : "s"}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </span>
      ),
    },
    {
      id: "valor",
      label: "Em negociação",
      width: 140,
      numeric: true,
      sortKey: "valor",
      cell: (row) => <span className="cx-data">{formatCurrency(row.value)}</span>,
    },
  ];

  if (!gate.ready) return <UnavailableIdentity reason={gate.reason!} degraded={gate.degraded} />;

  return (
    <div className="cx-layout cx-empresas">
      <SideNav
        label="Visões de empresas"
        groups={sideGroups}
        value={view}
        onChange={(id) => update({ visao: id === "todas" ? null : id })}
      />
      <section className="cx-work">
        <PageHead
          title="Empresas"
          sub={
            <>
              <b>{summary.companies}</b> {summary.companies === 1 ? "empresa" : "empresas"} nesta
              seleção, com <b>{summary.negotiating}</b> em negociação e{" "}
              <b>{formatCurrency(summary.value)}</b> em oportunidades abertas.
            </>
          }
          actions={
            <Button
              variant="primary"
              icon={<Plus size={15} aria-hidden="true" />}
              disabled={!gate.commandsReady}
              title={gate.commandsReady ? undefined : gate.reason}
              onClick={() => setCreating(true)}
            >
              Nova empresa
            </Button>
          }
        />

        {!gate.commandsReady && (
          <p className="cx-notice" data-tone="info" role="status">
            <Building2 size={15} aria-hidden="true" />
            <span>{gate.reason}</span>
          </p>
        )}

        <CandidateQueue />

        <Toolbar label="Filtros de empresas">
          <SearchInput
            value={query}
            onChange={(value) => update({ q: value || null })}
            label="Buscar por empresa, CNPJ, segmento ou cidade"
            placeholder="Buscar empresa, CNPJ, cidade…"
          />
          <Select
            options={[
              { value: "todos", label: "Todos os segmentos" },
              ...segments.map((item) => ({ value: item, label: item })),
            ]}
            value={segment}
            label="Segmento"
            onChange={(value) => update({ segmento: value === "todos" ? null : value })}
          />
          {(query || segment !== "todos") && (
            <Button variant="text" onClick={() => update({ q: null, segmento: null })}>
              Limpar filtros
            </Button>
          )}
          <ToolbarSpacer />
          <Select
            options={PAGE_SIZES.map((size) => ({ value: String(size), label: `${size} por página` }))}
            value={String(pageSize)}
            label="Itens por página"
            align="end"
            onChange={(value) => update({ tamanho: value })}
          />
        </Toolbar>

        <DataTable
          label="Empresas da seleção"
          regionLabel="Lista de empresas com rolagem"
          columns={columns}
          rows={visible}
          rowKey={(row) => row.company.id}
          rowLabel={(row) => `Abrir ${row.name}`}
          onOpenRow={(row) => navigate(`${appPath("empresas")}/${row.company.id}`)}
          sort={{ key: sortKey, direction: sortDirection }}
          onSort={(key) =>
            update({
              ordem: key,
              dir: key === sortKey && sortDirection === "asc" ? "desc" : "asc",
            })
          }
          actions={(row) => (
            <div className="cx-row-actions">
              <IconButton
                size="compact"
                label={`Abrir ${row.name}`}
                icon={<ExternalLink size={14} aria-hidden="true" />}
                onClick={() => navigate(`${appPath("empresas")}/${row.company.id}`)}
              />
            </div>
          )}
          empty={
            query || segment !== "todos" || view !== "todas" ? (
              <EmptyState
                variant="no-results"
                title="Nada nesta seleção"
                description="Os filtros atuais não alcançam nenhuma empresa."
                action={
                  <Button onClick={() => update({ q: null, segmento: null, visao: null })}>
                    Limpar filtros
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="Ainda não há empresas"
                description="Cadastre a primeira para organizar contatos, canais e oportunidades."
                action={
                  gate.commandsReady ? (
                    <Button variant="primary" onClick={() => setCreating(true)}>
                      Nova empresa
                    </Button>
                  ) : undefined
                }
              />
            )
          }
          footer={
            <>
              <span role="status">
                {sorted.length ? (current - 1) * pageSize + 1 : 0}–
                {Math.min(current * pageSize, sorted.length)} de <b>{sorted.length}</b> empresas
              </span>
              <div className="cx-pagination">
                <Button
                  size="compact"
                  disabled={current <= 1}
                  onClick={() => update({ pagina: String(current - 1) })}
                >
                  Anterior
                </Button>
                <span>
                  Página {current} de {pages}
                </span>
                <Button
                  size="compact"
                  disabled={current >= pages}
                  onClick={() => update({ pagina: String(current + 1) })}
                >
                  Próxima
                </Button>
              </div>
            </>
          }
        />

        <p className="cx-note">
          A busca alcança o histórico: uma empresa mesclada continua encontrável pelo nome antigo e
          aparece na visão Mescladas, somente leitura. Ordenação atual: {SORT_LABELS[sortKey]}.
        </p>
      </section>

      {creating && (
        <NewCompanyDialog
          onClose={() => setCreating(false)}
          onCreated={(companyId) => {
            setCreating(false);
            navigate(`${appPath("empresas")}/${companyId}`);
          }}
        />
      )}
    </div>
  );
}

/* --- Fila humana de candidatos ---------------------------------------------- */

function CandidateQueue() {
  const store = useStore();
  const gate = useIdentityGate();
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const pending = store.identityMatchCandidates.filter((candidate) => candidate.status === "pending");
  const reviewed = store.identityMatchCandidates.filter((candidate) => candidate.status !== "pending");
  const selected = reviewingId
    ? store.identityMatchCandidates.find((candidate) => candidate.id === reviewingId)
    : undefined;

  const name = (companyId?: string, contactId?: string) => {
    const company = store.companies.find((item) => item.id === companyId);
    if (company) return company.tradeName || company.legalName;
    const contact = store.contacts.find((item) => item.id === contactId);
    return contact?.name ?? "Registro referenciado";
  };

  // Sem conflito pendente, a fila não ocupa a tela inteira: vira uma linha que
  // continua dizendo o que ela faz (AGENTS §18).
  if (pending.length === 0)
    return (
      <section className="cx-candidates-quiet" aria-label="Fila humana de conflitos de identidade">
        <CheckCircle2 size={15} aria-hidden="true" />
        <span>
          <b>Nenhum conflito pendente.</b> Sugestões de identidade são evidências para decisão
          humana: aceitar não cria vínculo nem executa mesclagem.
        </span>
        {reviewed.length > 0 && (
          <details className="cx-candidate-history">
            <summary>Decisões registradas ({reviewed.length})</summary>
            <ul>
              {reviewed.map((candidate) => (
                <li key={candidate.id}>
                  <Pill tone={candidate.status === "accepted" ? "good" : "neutral"} dot>
                    {candidate.status === "accepted" ? "Aceita" : "Rejeitada"}
                  </Pill>
                  <span>
                    {name(candidate.companyId, candidate.contactId)} →{" "}
                    {name(candidate.candidateCompanyId, candidate.candidateContactId)}
                  </span>
                  <small>{candidate.reviewReason || "Sem motivo registrado"}</small>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    );

  return (
    <section className="cx-card cx-candidates" aria-label="Fila humana de conflitos de identidade">
      <header>
        <div>
          <small>FILA HUMANA</small>
          <h2>Possíveis conflitos de identidade</h2>
          <p>
            Sugestões são evidências para decisão humana. Aceitar não cria vínculo nem executa
            mesclagem.
          </p>
        </div>
        <span className="cx-count">{pending.length}</span>
      </header>

      <ul className="cx-candidate-list">
          {pending.map((candidate) => (
            <li key={candidate.id}>
              <div>
                <strong>
                  {name(candidate.companyId, candidate.contactId)} ↔{" "}
                  {name(candidate.candidateCompanyId, candidate.candidateContactId)}
                </strong>
                <small>{candidate.reason}</small>
              </div>
              <Button
                size="compact"
                disabled={!gate.commandsReady}
                title={gate.commandsReady ? undefined : gate.reason}
                onClick={() => setReviewingId(candidate.id)}
              >
                Revisar sugestão
              </Button>
          </li>
        ))}
      </ul>

      {reviewed.length > 0 && (
        <details className="cx-candidate-history">
          <summary>Decisões registradas ({reviewed.length})</summary>
          <ul>
            {reviewed.map((candidate) => (
              <li key={candidate.id}>
                <Pill tone={candidate.status === "accepted" ? "good" : "neutral"} dot>
                  {candidate.status === "accepted" ? "Aceita" : "Rejeitada"}
                </Pill>
                <span>
                  {name(candidate.companyId, candidate.contactId)} →{" "}
                  {name(candidate.candidateCompanyId, candidate.candidateContactId)}
                </span>
                <small>{candidate.reviewReason || "Sem motivo registrado"}</small>
              </li>
            ))}
          </ul>
        </details>
      )}

      {selected && (
        <ReviewCandidateDialog candidate={selected} onClose={() => setReviewingId(null)} />
      )}
    </section>
  );
}

/* --- Nova empresa / editar empresa -------------------------------------------- */

/**
 * O mesmo formulário cria e edita.
 *
 * "O resto pode ser completado depois" só é verdade se existir onde completar:
 * a identidade nasce com a razão social e ganha CNPJ, site, segmento e praça
 * quando eles aparecem. A conferência de CNPJ exato vale nos dois casos (§27.10).
 */
function NewCompanyDialog({
  company,
  onClose,
  onCreated,
}: {
  company?: Company;
  onClose: () => void;
  onCreated: (companyId: string) => void;
}) {
  const store = useStore();
  const [values, setValues] = useState({
    legalName: company?.legalName ?? "",
    tradeName: company?.tradeName ?? "",
    cnpj: company?.cnpj ?? "",
    site: company?.site ?? "",
    segment: company?.segment ?? "",
    city: company?.city ?? "",
    state: company?.state ?? "",
  });
  const [duplicate, setDuplicate] = useState<Company>();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (key: keyof typeof values) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));
  const dirty = company
    ? (Object.keys(values) as Array<keyof typeof values>).some(
        (key) => values[key].trim() !== ((company[key] as string | undefined) ?? ""),
      )
    : Object.values(values).some((value) => value.trim());

  const submit = async () => {
    if (saving) return;
    if (!values.legalName.trim()) {
      setError("Razão social é obrigatória.");
      return;
    }
    // CNPJ exato nunca reutiliza uma empresa sem confirmação humana (§27.10).
    const decision = values.cnpj ? companyCnpjDecision(store.companies, values.cnpj) : undefined;
    if (decision?.exact && decision.exact.id !== company?.id) {
      setDuplicate(decision.exact);
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (company) {
        const ok = await store.updateCompany(company.id, {
          legalName: values.legalName.trim(),
          tradeName: values.tradeName.trim() || undefined,
          cnpj: values.cnpj.trim() || undefined,
          site: values.site.trim() || undefined,
          segment: values.segment.trim() || undefined,
          city: values.city.trim() || undefined,
          state: values.state.trim() || undefined,
        });
        if (!ok) return;
        showFeedback("Empresa atualizada.", "success");
        onCreated(company.id);
        return;
      }
      const id = await store.addCompany({
        legalName: values.legalName.trim(),
        tradeName: values.tradeName.trim() || undefined,
        cnpj: values.cnpj.trim() || undefined,
        site: values.site.trim() || undefined,
        segment: values.segment.trim() || undefined,
        city: values.city.trim() || undefined,
        state: values.state.trim() || undefined,
      });
      if (!id) return;
      showFeedback("Empresa criada.", "success");
      onCreated(id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível salvar a empresa.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={dirty}
      title={company ? "Editar empresa" : "Nova empresa"}
      description="A razão social é o único campo obrigatório; o resto pode ser completado depois."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            {company ? "Salvar empresa" : "Criar empresa"}
          </Button>
        </>
      }
    >
      {duplicate && (
        <div className="cx-notice" data-tone="alert" role="alert">
          <Building2 size={15} aria-hidden="true" />
          <span>
            <b>Este CNPJ já pertence a {duplicate.tradeName || duplicate.legalName}.</b> Confirme o
            uso da empresa existente; nada é mesclado automaticamente.
          </span>
          <Button size="compact" onClick={() => onCreated(duplicate.id)}>
            Usar existente
          </Button>
          <Button size="compact" variant="text" onClick={() => setDuplicate(undefined)}>
            Revisar CNPJ
          </Button>
        </div>
      )}

      <div className="cx-form-grid">
        <Field label="Razão social" required className="cx-span-2" error={error || undefined}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={values.legalName}
              placeholder="Nome jurídico da empresa"
              onChange={(event) => {
                setError("");
                set("legalName")(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Nome fantasia">
          {({ id }) => (
            <TextInput
              id={id}
              value={values.tradeName}
              placeholder="Como o negócio é conhecido"
              onChange={(event) => set("tradeName")(event.target.value)}
            />
          )}
        </Field>
        <Field label="CNPJ" help="Opcional. Único quando informado.">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={values.cnpj}
              onChange={(event) => {
                setDuplicate(undefined);
                set("cnpj")(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Segmento">
          {({ id }) => (
            <TextInput
              id={id}
              value={values.segment}
              placeholder="Ex.: saúde"
              onChange={(event) => set("segment")(event.target.value)}
            />
          )}
        </Field>
        <Field label="Site">
          {({ id }) => (
            <TextInput
              id={id}
              value={values.site}
              placeholder="https://"
              onChange={(event) => set("site")(event.target.value)}
            />
          )}
        </Field>
        <Field label="Cidade">
          {({ id }) => (
            <TextInput
              id={id}
              value={values.city}
              onChange={(event) => set("city")(event.target.value)}
            />
          )}
        </Field>
        <Field label="UF">
          {({ id }) => (
            <TextInput
              id={id}
              maxLength={2}
              value={values.state}
              onChange={(event) => set("state")(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/* --- Detalhe da empresa ------------------------------------------------------ */

function Panel({
  title,
  count,
  children,
  action,
}: {
  title: string;
  count?: number;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="cx-card cx-panel">
      <header>
        <h2>{title}</h2>
        {count !== undefined && <span className="cx-count">{count}</span>}
        {action}
      </header>
      {children}
    </section>
  );
}

function channelIcon(channel: PartyChannel) {
  if (channel.type === "email") return <Mail size={14} aria-hidden="true" />;
  if (channel.type === "phone") return <Phone size={14} aria-hidden="true" />;
  return <Globe2 size={14} aria-hidden="true" />;
}

export function EmpresaPage() {
  const store = useStore();
  const gate = useIdentityGate();
  const navigate = useNavigate();
  const { companyId = "" } = useParams();
  const [contactOpen, setContactOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [channelId, setChannelId] = useState<string | null>(null);

  const company = store.companies.find((item) => item.id === companyId);

  if (!gate.ready) return <UnavailableIdentity reason={gate.reason!} degraded={gate.degraded} />;

  if (!company)
    return (
      <div className="cx-empresas">
        <section className="cx-work">
          <PageHead title="Empresa não encontrada" />
          <EmptyState
            title="Esta empresa não está disponível"
            description="Ela pode ter sido mesclada, removida ou nunca ter existido neste ambiente."
            action={
              <Button onClick={() => navigate(appPath("empresas"))}>Voltar para Empresas</Button>
            }
          />
        </section>
      </div>
    );

  const contacts = contactsInCompanyHistory(store.companies, store.contacts, company.id);
  const channels = channelsInCompanyHistory(store.companies, store.partyChannels, company.id);
  const leads = leadsInCompanyHistory(store.companies, store.leads, company);
  const memberships = store.memberships.filter((membership) =>
    leads.some((lead) => lead.id === membership.leadId),
  );
  const tombstone = company.status !== "active";
  const target = tombstone && company.mergedIntoId
    ? store.companies.find((item) => item.id === company.mergedIntoId)
    : undefined;
  const mergeTargets = store.companies.filter(
    (item) => item.id !== company.id && item.status === "active",
  );

  const writeReason = tombstone
    ? "Esta identidade foi mesclada: seus dados históricos são somente leitura."
    : gate.reason;
  const canWrite = gate.commandsReady && !tombstone;
  const canMerge = canWrite && mergeTargets.length > 0;
  const selectedChannel = channelId
    ? channels.find((channel) => channel.id === channelId && channel.companyId === company.id)
    : undefined;

  return (
    <div className="cx-empresas cx-empresa-detalhe">
      <section className="cx-work">
        <Button
          variant="text"
          size="compact"
          icon={<ArrowLeft size={14} aria-hidden="true" />}
          className="cx-back"
          onClick={() => navigate(appPath("empresas"))}
        >
          Empresas
        </Button>

        <PageHead
          title={company.tradeName || company.legalName}
          sub={
            <>
              {company.legalName !== (company.tradeName || company.legalName) && (
                <>{company.legalName} · </>
              )}
              <b>{leads.length}</b> {leads.length === 1 ? "oportunidade" : "oportunidades"}, com{" "}
              <b>{memberships.filter((item) => item.status === "active").length}</b> em negociação e{" "}
              <b>{memberships.filter((item) => item.status === "won").length}</b> ganha(s).
            </>
          }
          actions={
            <>
              <Button
                icon={<Pencil size={15} aria-hidden="true" />}
                disabled={!canWrite}
                title={canWrite ? undefined : writeReason}
                onClick={() => setEditOpen(true)}
              >
                Editar empresa
              </Button>
              <Button
                icon={<Plus size={15} aria-hidden="true" />}
                disabled={!canWrite}
                title={canWrite ? undefined : writeReason}
                onClick={() => setContactOpen(true)}
              >
                Novo contato
              </Button>
              <Button
                icon={<GitMerge size={15} aria-hidden="true" />}
                disabled={!canMerge}
                title={
                  canMerge
                    ? undefined
                    : mergeTargets.length === 0
                      ? "Cadastre outra empresa ativa antes de iniciar uma mesclagem."
                      : writeReason
                }
                onClick={() => setMergeOpen(true)}
              >
                Mesclar empresa
              </Button>
            </>
          }
        />

        {tombstone && (
          <p className="cx-notice" data-tone="info" role="status">
            <GitMerge size={15} aria-hidden="true" />
            <span>
              <b>Identidade mesclada.</b> O histórico continua preservado e é agregado no destino.
              {target && (
                <>
                  {" "}
                  <Button
                    variant="text"
                    size="compact"
                    onClick={() => navigate(`${appPath("empresas")}/${target.id}`)}
                  >
                    Abrir {target.tradeName || target.legalName}
                  </Button>
                </>
              )}
            </span>
          </p>
        )}

        <div className="cx-empresa-grid">
          <Panel title="Dados da empresa">
            <dl className="cx-facts">
              <div>
                <dt>CNPJ</dt>
                <dd className="cx-data">{company.cnpj || "Não informado"}</dd>
              </div>
              <div>
                <dt>Segmento</dt>
                <dd>{company.segment || "Não informado"}</dd>
              </div>
              <div>
                <dt>Localização</dt>
                <dd>{[company.city, company.state].filter(Boolean).join(" / ") || "Não informada"}</dd>
              </div>
              <div>
                <dt>Site</dt>
                <dd>
                  {company.site ? (
                    <a href={company.site} target="_blank" rel="noreferrer noopener">
                      {company.site}
                    </a>
                  ) : (
                    "Não informado"
                  )}
                </dd>
              </div>
            </dl>
          </Panel>

          <Panel title="Contatos" count={contacts.length}>
            {contacts.length ? (
              <ul className="cx-contact-list">
                {contacts.map((contact) => (
                  <li key={contact.id}>
                    <Avatar name={contact.name} />
                    <span>
                      <strong>{contact.name}</strong>
                      <small>{contact.role || "Função não informada"}</small>
                    </span>
                    <span className="cx-contact-channels">
                      {channels
                        .filter((channel) => channel.contactId === contact.id)
                        .slice(0, 2)
                        .map((channel) => (
                          <span key={channel.id}>
                            {channelIcon(channel)}
                            {channel.value}
                          </span>
                        ))}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                icon={<Users size={18} aria-hidden="true" />}
                title="Nenhum contato cadastrado"
                description="Um lead pode existir sem contato, mas uma oportunidade com pessoas vinculadas exige um contato primário."
              />
            )}
          </Panel>

          <Panel title="Canais" count={channels.length}>
            {channels.length ? (
              <ul className="cx-channel-list">
                {channels.map((channel) => {
                  const historical = channel.companyId !== company.id;
                  const owner = channel.contactId
                    ? contacts.find((contact) => contact.id === channel.contactId)?.name
                    : "Canal geral da empresa";
                  return (
                    <li key={channel.id}>
                      {channelIcon(channel)}
                      <span>
                        <strong>{channel.value}</strong>
                        <small>{historical ? "Histórico da origem" : owner}</small>
                      </span>
                      <Button
                        size="compact"
                        variant="text"
                        disabled={!canWrite || historical}
                        title={
                          historical
                            ? "Canal histórico da origem; somente leitura."
                            : canWrite
                              ? undefined
                              : writeReason
                        }
                        aria-label={`Reclassificar canal ${channel.value}`}
                        onClick={() => setChannelId(channel.id)}
                      >
                        Reclassificar
                      </Button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                icon={<Mail size={18} aria-hidden="true" />}
                title="Nenhum canal cadastrado"
                description="Canais aparecem aqui quando pertencem à empresa ou a um contato dela."
              />
            )}
          </Panel>

          <Panel title="Oportunidades" count={leads.length}>
            {leads.length ? (
              <ul className="cx-opportunity-list">
                {leads.map((lead) => {
                  const membership = memberships.find((item) => item.leadId === lead.id);
                  const funnel = store.funnels.find((item) => item.id === membership?.funnelId);
                  const identity = identityContextForLead(
                    store.companies,
                    store.contacts,
                    store.partyChannels,
                    lead,
                  );
                  return (
                    <li key={lead.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`${appPath("leads")}/${lead.id}`)}
                      >
                        <span>
                          <strong>{identity.displayContact || lead.name}</strong>
                          <small>
                            {funnel?.name || "Funil não identificado"} ·{" "}
                            {formatCurrency(lead.potentialValue)}
                          </small>
                        </span>
                        <Pill
                          tone={
                            membership?.status === "won"
                              ? "good"
                              : membership?.status === "lost"
                                ? "crit"
                                : "accent"
                          }
                          dot
                        >
                          {membership?.status === "won"
                            ? "Ganha"
                            : membership?.status === "lost"
                              ? "Perdida"
                              : "Em negociação"}
                        </Pill>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                icon={<Handshake size={18} aria-hidden="true" />}
                title="Nenhuma oportunidade"
                description="Crie um lead pelo funil para abrir a primeira oportunidade desta empresa."
              />
            )}
          </Panel>
        </div>

        <p className="cx-note">
          Cada oportunidade mantém a própria jornada, etapa e desfecho. Esta tela agrega a
          identidade da empresa; ela não junta históricos de funis diferentes.
        </p>
      </section>

      {editOpen && (
        <NewCompanyDialog
          company={company}
          onClose={() => setEditOpen(false)}
          onCreated={() => setEditOpen(false)}
        />
      )}
      {contactOpen && (
        <NewContactDialog companyId={company.id} onClose={() => setContactOpen(false)} />
      )}
      {mergeOpen && (
        <MergeCompaniesDialog
          source={company}
          onClose={() => setMergeOpen(false)}
          onCompleted={(targetCompanyId) => {
            setMergeOpen(false);
            navigate(`${appPath("empresas")}/${targetCompanyId}`);
          }}
        />
      )}
      {selectedChannel && (
        <ReclassifyChannelDialog
          channel={selectedChannel}
          company={company}
          contacts={contacts}
          onClose={() => setChannelId(null)}
        />
      )}
    </div>
  );
}

function NewContactDialog({ companyId, onClose }: { companyId: string; onClose: () => void }) {
  const store = useStore();
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (saving) return;
    if (!name.trim()) {
      setError("Nome é obrigatório.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const id = await store.addContact({ companyId, name: name.trim(), role: role.trim() || undefined });
      if (!id) return;
      showFeedback("Contato criado.", "success");
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível criar o contato.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={Boolean(name.trim() || role.trim())}
      title="Novo contato"
      description="A pessoa pertence permanentemente a esta empresa e não pode ser transferida."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            Criar contato
          </Button>
        </>
      }
    >
      <div className="cx-form-grid">
        <Field label="Nome" required className="cx-span-2" error={error || undefined}>
          {({ id, describedBy, invalid }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={name}
              placeholder="Nome completo"
              onChange={(event) => {
                setError("");
                setName(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label="Função" className="cx-span-2">
          {({ id }) => (
            <TextInput
              id={id}
              value={role}
              placeholder="Ex.: sócia, marketing"
              onChange={(event) => setRole(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
